import { Badge, Button, Flex, Form, FormListFieldData, Input, InputNumber, InputNumberProps, Switch, Tooltip, theme } from 'antd';
import { FC } from 'react';
import { LockFill, Trash3Fill, UnlockFill } from 'react-bootstrap-icons';

import { ReturnArrowIcon } from '@/common/components';
import { defineInputStatus } from '@/common/utils';

import { ReferralLinksFormFields, ReferralLinksFormInputs } from '@/modules/Sites';

import { FormListOperationsType, ReferralLinksEditFormViewProps } from './ReferralLinksEditFormView';

interface Props {
  initialValues: ReferralLinksEditFormViewProps['initialValues'];
  form: ReferralLinksEditFormViewProps['form'];
  formError: ReferralLinksEditFormViewProps['formErrors'];
  isFormLoading: ReferralLinksEditFormViewProps['isFormLoading'];
  item: FormListFieldData;
  remove: FormListOperationsType['remove'];
}


export const ReferralLinksEditFormViewItem: FC<Props> = ({
  form,
  formError,
  isFormLoading,
  item,
  initialValues,
  remove
}) => {
  const { token: { colorErrorBg } } = theme.useToken();
  const currentElement = Form.useWatch(['links', item.name], form);

  const itemField = (field: keyof typeof currentElement): Parameters<typeof form.setFieldValue>[0] => ['links', item.name, field];
  const fieldError = (field: keyof typeof currentElement): string | undefined =>
    formError?.field === field ? formError.message : undefined;

  const handleChangeFrequency: NonNullable<InputNumberProps['onChange']> = (value) => {
    const transformedValue = value === null ? null : +Number(value).toFixed(2);
    form.setFieldValue(itemField('appearanceFrequency'), transformedValue);
    reviewAllItemsAppearanceFrequency(form);
  };

  const handleMarkAsDeleted = () => {
    if (currentElement?.isNew) remove(item.name);
    else {

      const isRemoved = !currentElement?.isRemoved;
      const value = isRemoved ? 0 : (initialValues.links.find(({ id }) => id === currentElement?.id))?.appearanceFrequency ?? 0;

      form.setFieldValue(itemField('isRemoved'), isRemoved);
      form.setFieldValue(itemField('appearanceFrequency'), value);
    }
    reviewAllItemsAppearanceFrequency(form);
  };

  const handleLockFrequency = () => {
    const isLocked = !currentElement?.isFrequencyLocked;
    form.setFieldValue(itemField('isFrequencyLocked'), isLocked);
    reviewAllItemsAppearanceFrequency(form);
  };

  const handleChangeIsActive = (value: boolean) => {
    form.setFieldValue(itemField('isActive'), value);
    reviewAllItemsAppearanceFrequency(form);
  };

  return (
    <Badge.Ribbon
      style={{ display: currentElement?.isNew ? 'unset' : 'none' }}
      text={ currentElement?.isNew ? 'New' : undefined } color="green" placement="end"
    >
      <Flex
        gap={ 8 }
        align="center"
        style={{
          padding: 8,
          background: currentElement?.isRemoved ? colorErrorBg : '',
          borderRadius: 4
        }}
      >
        <Button
          danger={ !currentElement?.isRemoved }
          type={ currentElement?.isRemoved ? 'default' : 'primary' }
          shape="round"
          icon={ currentElement?.isRemoved ? <ReturnArrowIcon/> : <Trash3Fill/> }
          onClick={ handleMarkAsDeleted }
          style={{ paddingInline: 8 }}
          loading={ isFormLoading }
        />

        <div style={{ width: '100%' }}>
          <Form.Item hidden name={ [item.name, ReferralLinksFormInputs.Id] }/>
          <Form.Item hidden name={ [item.name, ReferralLinksFormInputs.IsNew] }/>
          <Form.Item hidden name={ [item.name, ReferralLinksFormInputs.IsRemoved] }/>
          <Form.Item hidden name={ [item.name, ReferralLinksFormInputs.SiteId] }/>
          <Form.Item hidden name={ [item.name, ReferralLinksFormInputs.ReferralId] }/>

          <Form.Item
            label="URL"
            name={ [item.name, ReferralLinksFormInputs.Url] }
            hasFeedback
            style={{ marginBottom: 5 }}
            help={ fieldError('url') }
            validateStatus={ defineInputStatus(isFormLoading, fieldError('url')) }
          >
            <Input
              allowClear
              placeholder="Введіть URL посилання"
              disabled={
                isFormLoading ||
                currentElement?.isRemoved ||
                !currentElement?.isActive
              }
            />
          </Form.Item>

          <Form.Item noStyle style={{ marginBottom: 5 }}>
            <Flex gap={ 8 } align="center">
              <Form.Item
                label="Частота появлень"
                name={ [item.name, ReferralLinksFormInputs.AppearanceFrequency] }
                hasFeedback
                style={{ marginBottom: 0 }}
                help={ fieldError('appearanceFrequency') }
                validateStatus={ defineInputStatus(isFormLoading, fieldError('appearanceFrequency')) }
              >
                <InputNumber
                  style={{ width: 140 }}
                  max={ 100 }
                  min={ 0 }
                  addonAfter="%"
                  onChange={ handleChangeFrequency }
                  disabled={
                    isFormLoading ||
                    currentElement?.isRemoved ||
                    currentElement?.isFrequencyLocked ||
                    !currentElement?.isActive
                  }
                />
              </Form.Item>

              <Form.Item noStyle name={ [item.name, ReferralLinksFormInputs.IsFrequencyLocked] }>
                <Tooltip title="Автоматичне значення" placement="bottom">
                  <Button
                    disabled={ isFormLoading || currentElement?.isRemoved || !currentElement?.isActive }
                    type="link"
                    icon={ currentElement?.isFrequencyLocked ? <LockFill/> : <UnlockFill/> }
                    onClick={ handleLockFrequency }
                  />
                </Tooltip>
              </Form.Item>
            </Flex>
          </Form.Item>

          <Form.Item
            label="Активне"
            name={ [item.name, ReferralLinksFormInputs.IsActive] }
            hasFeedback
            style={{ marginBottom: 5 }}
            help={ fieldError('isActive') }
            validateStatus={ defineInputStatus(isFormLoading, fieldError('isActive')) }
          >
            <Switch
              disabled={ isFormLoading || currentElement?.isRemoved }
              onChange={ handleChangeIsActive }
            />
          </Form.Item>
        </div>
      </Flex>
    </Badge.Ribbon>

  );
};

const reviewAllItemsAppearanceFrequency = (form: Props['form']) => {
  const links: ReferralLinksFormFields['links'] = form.getFieldValue('links');
  const activeAndLocked = links.filter(
    ({ isActive, isFrequencyLocked }) => isActive && isFrequencyLocked
  );
  const activeAndUnlocked = links.filter(
    ({ isActive, isFrequencyLocked }) => isActive && !isFrequencyLocked
  );
  const activeAndLockedCount = activeAndLocked.length;
  const activeAndUnlockedValue = activeAndUnlocked.reduce((prev, curr) => prev + curr.appearanceFrequency, 0);

  form.setFieldsValue({
    links: links?.map((link) => {
      if (!link.isFrequencyLocked) {
        return {
          ...link,
          appearanceFrequency: link.isActive ? link.appearanceFrequency : 0
        };
      };

      const appearanceFrequency = link.isActive
        ? +((100 - activeAndUnlockedValue) / activeAndLockedCount).toFixed(2)
        : 0;

      return {
        ...link,
        appearanceFrequency,
        isActive: link.isRemoved || !link.isActive ? false : appearanceFrequency > 0
      };
    }) ?? []
  });
};
